import React, {useCallback} from 'react';

import InternalLink from 'notebook/mediocre/mentions/InternalLink';
import {ArrayTypedValue, TypedValue} from 'notebook/tables/TableConstants';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import ControlledSearchInput from 'schemas/tags/ControlledSearchInput';
import {ElementKeyboardEvent} from 'util/Types';

export interface ArrayEditorProps {
  autoFocus: boolean;
  inputValue: string;
  onChange: (finalizedValues: Array<TypedValue>) => void;
  onBlur?: () => void;
  onUpdateInputValue: (inputValue: string) => void;
  onKeyDown: (e: ElementKeyboardEvent) => void;
  typedValue: ArrayTypedValue;
  // This will typically be rendered inside a table cell. If that's not the case, set this to true
  isOutsideTable?: boolean;
}

export default function ArrayEditor(props: ArrayEditorProps): JSX.Element {
  const {typedValue, onUpdateInputValue, onChange, isOutsideTable} = props;

  const handleUpdateInputValue = useCallback(
    (inputValue: string) => {
      onUpdateInputValue(inputValue);
    },
    [onUpdateInputValue]
  );

  const handleRemoveLastValue = useCallback((): void => {
    if (typedValue.rawValue.length > 0) {
      onChange(typedValue.rawValue.slice(0, typedValue.rawValue.length - 1));
    }
  }, [typedValue, onChange]);

  const renderSelectedValues = useCallback(() => {
    const Wrapper = isOutsideTable ? 'span' : 'div';
    const stringValues = typedValue.rawValue.map((value) => formatTypedValue(value));
    return (
      <Wrapper>
        {stringValues.map((value, index: number) => (
          <span key={index}>
            <InternalLink
              className="chip-container chip--tableCell"
              internalLink={{name: value}}
              onRemove={() => {
                onChange(typedValue.rawValue.filter((_, i) => i !== index));
              }}
            />
          </span>
        ))}
      </Wrapper>
    );
  }, [typedValue, isOutsideTable, onChange]);

  return (
    <ControlledSearchInput
      dataTestAttrs={{component: 'ArrayEditor', element: 'root'}}
      shouldForwardKeydown={true}
      canSelectAdditional={true}
      hasTypedValue={typedValue.rawValue.length > 0}
      onRemoveLastValue={handleRemoveLastValue}
      autoFocus={props.autoFocus}
      inputValue={props.inputValue}
      onBlur={props.onBlur}
      onUpdateInputValue={handleUpdateInputValue}
      onKeyDown={props.onKeyDown}
      renderSelectedValues={renderSelectedValues}
      shouldCloseOnInsideClick={false}
      isOutsideTable={props.isOutsideTable}
      showCaret={props.isOutsideTable}
      placeholder=""
    />
  );
}
